// Pure factory: build a checker for the running build's `buildId` against
// a tiny static manifest served at /frontend-version.json. Returns null
// on every error path — the banner never appears because of a transient
// network blip.
//
// Every side effect is injectable so the unit tests can drive it with
// fakes (MemoryStorage, vi.fn fetcher, controllable now/timers, and
// in-memory EventTarget instances).

const DEFAULT_VERSION_URL = '/frontend-version.json'
const DEFAULT_CHECK_INTERVAL_MS = 5 * 60 * 1000
const DEFAULT_SNOOZE_MS = 60 * 60 * 1000
const SNOOZE_STORAGE_KEY = 'prufa:frontend-update-snooze'

function defaultStorage(storageImpl) {
  return storageImpl === undefined ? null : storageImpl
}

function defaultNow() {
  return Date.now()
}

function defaultWindowTarget(win) {
  if (!win) return null
  return {
    addEventListener: win.addEventListener ? win.addEventListener.bind(win) : null,
    removeEventListener: win.removeEventListener ? win.removeEventListener.bind(win) : null,
  }
}

function defaultDocumentTarget(doc) {
  if (!doc) return null
  return {
    visibilityState: doc.visibilityState,
    addEventListener: doc.addEventListener ? doc.addEventListener.bind(doc) : null,
    removeEventListener: doc.removeEventListener ? doc.removeEventListener.bind(doc) : null,
  }
}

function appendCacheBust(url, now) {
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}t=${now}`
}

function parseBuildId(payload) {
  if (!payload || typeof payload !== 'object') return null
  const value = payload.buildId
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function readSnooze(storage, now) {
  if (!storage) return null
  let raw = null
  try {
    raw = storage.getItem(SNOOZE_STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null

  let parsed
  try {
    parsed = JSON.parse(raw)
  } catch {
    try { storage.removeItem(SNOOZE_STORAGE_KEY) } catch { /* ignore */ }
    return null
  }

  if (!parsed || typeof parsed.buildId !== 'string' || typeof parsed.until !== 'number') {
    try { storage.removeItem(SNOOZE_STORAGE_KEY) } catch { /* ignore */ }
    return null
  }

  if (parsed.until <= now) {
    try { storage.removeItem(SNOOZE_STORAGE_KEY) } catch { /* ignore */ }
    return null
  }

  return parsed
}

function isSnoozed(storage, buildId, now) {
  const record = readSnooze(storage, now)
  return record?.buildId === buildId
}

export function createFrontendVersionChecker(options) {
  const enabled = options.enabled !== false
  const currentBuildId = options.currentBuildId ?? ''
  const versionUrl = options.versionUrl ?? DEFAULT_VERSION_URL
  const checkIntervalMs = options.checkIntervalMs ?? DEFAULT_CHECK_INTERVAL_MS
  const snoozeMs = options.snoozeMs ?? DEFAULT_SNOOZE_MS
  const fetcher = options.fetcher ?? (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null)
  const storage = options.storage === undefined ? defaultStorage() : options.storage
  const now = options.now ?? defaultNow
  const setIntervalFn = options.setIntervalFn ?? setInterval
  const clearIntervalFn = options.clearIntervalFn ?? clearInterval
  const windowTarget = options.windowTarget === undefined
    ? defaultWindowTarget(typeof window !== 'undefined' ? window : null)
    : options.windowTarget
  const documentTarget = options.documentTarget === undefined
    ? defaultDocumentTarget(typeof document !== 'undefined' ? document : null)
    : options.documentTarget

  async function check() {
    if (!enabled || !currentBuildId || !fetcher) return null

    let response
    try {
      response = await fetcher(appendCacheBust(versionUrl, now()), {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      })
    } catch {
      return null
    }

    if (!response || !response.ok) return null

    let payload
    try {
      payload = await response.json()
    } catch {
      return null
    }

    const latestBuildId = parseBuildId(payload)
    if (!latestBuildId || latestBuildId === currentBuildId) return null
    if (isSnoozed(storage, latestBuildId, now())) return null

    return { currentBuildId, latestBuildId }
  }

  function snooze(buildId) {
    if (!buildId || !storage) return
    try {
      storage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify({ buildId, until: now() + snoozeMs }))
    } catch {
      // Storage can fail in private browsing or strict browser settings.
    }
  }

  function start(onUpdate) {
    if (!enabled) return () => undefined

    let stopped = false
    const runCheck = () => {
      check().then((update) => {
        if (!stopped && update) onUpdate(update)
      })
    }
    const handleFocus = () => runCheck()
    const handleVisibilityChange = () => {
      if (!documentTarget || documentTarget.visibilityState === 'visible') runCheck()
    }

    runCheck()
    const intervalId = setIntervalFn(runCheck, checkIntervalMs)
    windowTarget?.addEventListener?.('focus', handleFocus)
    documentTarget?.addEventListener?.('visibilitychange', handleVisibilityChange)

    return () => {
      stopped = true
      clearIntervalFn(intervalId)
      windowTarget?.removeEventListener?.('focus', handleFocus)
      documentTarget?.removeEventListener?.('visibilitychange', handleVisibilityChange)
    }
  }

  return { check, snooze, start }
}
